import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { useToast } from "../lib/toast.js";
import { formatReviewPercent } from "../lib/assumptions-review-format.js";
import {
  applyOptimisticDimensionConfirmation,
  dimensionConfirmationForProduct,
  type DimensionConfirmation,
} from "../lib/assumptions-review-confirmation.js";

type AssumptionStatus = "confirmed" | "estimate" | "unverified" | "missing" | "conflict";

interface Source {
  label: string;
  status: AssumptionStatus;
  detail?: string;
  effectiveDate?: string;
}

export type SupplierPaymentAnchor = "at_po" | "after_po" | "after_arrival" | "after_sale";

export interface SupplierPaymentSchedule {
  tranches: Array<{
    percent: number;
    offsetDays: number;
    anchor: SupplierPaymentAnchor;
  }>;
  source: Source;
}

interface Finding {
  id: string;
  severity: "hard_blocker" | "warning";
  code: string;
  title: string;
  detail: string;
}

interface OtherCost {
  name: string;
  amount: number;
  kind: "fixed" | "pct";
  unit: "currency" | "percent";
  firstOrderOnly?: boolean;
  platforms?: string[];
  source?: Source;
}

interface ReviewSku {
  skuId: string;
  skuCode: string;
  rawProductName: string;
  rawVariantName: string;
  sold: boolean;
  orders: number;
  revenue: number;
  soldPacks: number;
  physicalUnits: number | null;
  classification: {
    status: "needs_review" | "confirmed" | "excluded" | "retired" | "missing";
    familyStatus: "needs_review" | "confirmed" | null;
    unitsStatus: "needs_review" | "confirmed" | null;
    packSource: "explicit" | "multiplier" | "bare" | "default_single" | "rule" | "manual" | null;
    source: string;
    confidence: number | null;
    packSize: number | null;
    offerLabel: string | null;
    offerMultiplier: number;
    variantLabel: string | null;
    unitsPerSale: number | null;
    familyReviewBasis: "human" | "reviewed_alias" | "rule" | "unreviewed";
    unitsReviewBasis: "human" | "product_name" | "rule" | "unreviewed";
  };
  source: Source;
  cmInput: {
    effectiveDate: string;
    currency: string;
    productCostPerSale: number;
    fulfillmentShippingPerSale: number;
    fulfillmentByPlatform: Record<string, number> | null;
    pickPackPerSale: number;
    returnsProcessingPerSale: number;
    inboundFreightPerSale: number;
    laborPerSale: number;
    storageFeePerMonth: number;
    processingPercent: number;
    processingFixedPerSale: number;
    refundRatePercent: number;
    otherCosts: OtherCost[];
    activeInputCount: number;
  } | null;
  productCostSource: Source;
  fulfillmentEvidence: Array<{
    platform: string;
    workbookName: string;
    sheetName: string;
    shipping: { header: string; amount: number };
    packaging: { header: string; amount: number };
    total: number;
  }>;
  math: {
    unitsPerSale: number | null;
    productCostPerTrueUnit: number | null;
    productCostPerSale: number | null;
    fulfillmentShippingPerSale: number | null;
    knownFixedVariableCostsPerSale: number | null;
    processingPercent: number | null;
    processingFixedPerSale: number | null;
  };
  inventoryTiming: {
    leadTimeDays: number;
    paymentTermsDays: number;
    effectiveDate: string;
    supplierName: string | null;
    source: Source;
  } | null;
  bundleComponents: Array<{
    id: string;
    label: string;
    quantity: number;
    componentSkuId: string | null;
    componentProductGroupId: string | null;
  }>;
  findings: Finding[];
}

interface ReviewProduct {
  groupId: string;
  name: string;
  aliases: string[];
  reviewedAliases: Array<{
    alias: string;
    matchType: "exact" | "prefix";
    source: "manual" | "merge";
    reviewedAt: string;
  }>;
  kind: string;
  type: string | null;
  unitLabel: string;
  soldSkuCount: number;
  skuCount: number;
  orders: number;
  revenue: number;
  savedRules: {
    fulfillmentFlat: boolean;
    fulfillmentFormula: { base: number; perExtraUnit: number } | null;
    pendingCatalogAssumptions: boolean;
    packagingShownSeparately: boolean;
    supplierPaymentOverride: SupplierPaymentSchedule | null;
  };
  skus: ReviewSku[];
  findings: Finding[];
}

interface AssumptionsReview {
  accountId: string;
  accountName: string;
  currency: string;
  generatedAt: string;
  onboarding: {
    phase: string | null;
    confirmed: boolean;
    updatedAt: string | null;
    unresolvedWorkbookQuestions: number;
    workbookQuestions?: Array<{ id: string; question: string }>;
  };
  summary: {
    productCount: number;
    skuCount: number;
    soldSkuCount: number;
    hardBlockers: number;
    warnings: number;
    estimates: number;
    sourceNotRecorded: number;
  };
  global: {
    overhead: Array<{
      amount: number;
      currency: string;
      effectiveDate: string;
      source: Source;
      notes: string | null;
      approvedAt: string | null;
    }>;
    processing: {
      blendedPercent: number;
      blendedFixed: number;
      gatewayMonthly: number;
      processors: Array<{
        name: string;
        percent: number;
        fixed: number;
        sharePercent: number;
        aliases: string[];
        platforms: string[];
        basis: "exact" | "blended" | "estimate" | null;
        source: Source;
      }>;
      source: Source;
    } | null;
    otherCosts: Array<{
      id: string;
      name: string;
      amount: number;
      kind: "fixed" | "pct";
      unit: "currency" | "percent";
      scope: "all" | string[];
      source: Source;
    }>;
    payout: {
      settlementDays: number;
      reservePercent: number;
      reserveDays: number;
      processors: Array<{
        name: string;
        sharePercent: number;
        settlementDays: number | null;
        reservePercent: number;
        reserveDays: number;
      }>;
      source: Source;
    } | null;
    inventoryFinancing: {
      financedPercent: number;
      annualRatePercent: number;
      source: Source;
    } | null;
    supplierSchedule: SupplierPaymentSchedule & {
      supplierCardFloatDays: number | null;
    } | null;
    parkedCosts: string[];
  };
  economicsLinks: Array<{
    sourceGroupId: string;
    sourceGroupName: string;
    targetGroupId: string;
    targetGroupName: string;
    copyProductCost: boolean;
    copyFulfillmentShipping: boolean;
  }>;
  products: ReviewProduct[];
  ungroupedSkus: ReviewSku[];
  findings: Finding[];
}

export interface AssumptionsReviewReadiness {
  status: "not_loaded" | "loading" | "loaded" | "error";
  hardBlockers: number;
  warningIds: string[];
  acceptedWarningIds: string[];
  reviewNote: string;
}

export function AgencyAssumptionsReview({ accountId, onReadinessChange }: {
  accountId: string;
  onReadinessChange?: (readiness: AssumptionsReviewReadiness) => void;
}): React.ReactElement {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [openProduct, setOpenProduct] = useState<string | null>(null);
  const [acceptedWarningIds, setAcceptedWarningIds] = useState<Set<string>>(new Set());
  const [reviewNote, setReviewNote] = useState("");
  const review = useQuery({
    queryKey: ["admin", "assumptions", accountId],
    // This report scans a full client catalog. Keep the loaded review stable
    // until the operator explicitly refreshes it or leaves the cached page.
    staleTime: Infinity,
    enabled: open,
    queryFn: () => api.get<AssumptionsReview>(`/admin/clients/${accountId}/assumptions`),
  });
  const warningFindings = review.data?.findings.filter((finding) => finding.severity === "warning") ?? [];
  const warningFingerprint = warningFindings
    .map((finding) => `${finding.id}:${finding.detail}`)
    .sort()
    .join("|");
  useEffect(() => {
    setAcceptedWarningIds(new Set());
    setReviewNote("");
  }, [warningFingerprint]);
  const warningIds = warningFindings.map((finding) => finding.id).sort();
  const acceptedIds = [...acceptedWarningIds].sort();
  useEffect(() => {
    if (!onReadinessChange) return;
    const acceptance = { warningIds, acceptedWarningIds: acceptedIds, reviewNote };
    if (review.isError) onReadinessChange({ status: "error", hardBlockers: 0, ...acceptance });
    else if (review.data) onReadinessChange({ status: "loaded", hardBlockers: review.data.summary.hardBlockers, ...acceptance });
    else if (open) onReadinessChange({ status: "loading", hardBlockers: 0, ...acceptance });
    else onReadinessChange({ status: "not_loaded", hardBlockers: 0, ...acceptance });
  }, [acceptedIds.join("|"), onReadinessChange, open, review.data, review.isError, reviewNote, warningIds.join("|")]);
  const confirmDimension = useMutation({
    mutationFn: (input: DimensionConfirmation) => api.patch(
      `/admin/clients/${accountId}/catalog/review-dimensions`,
      {
        skuIds: input.skuIds,
        ...(input.familyStatus ? { familyStatus: input.familyStatus } : {}),
        ...(input.unitsStatus ? { unitsStatus: input.unitsStatus } : {}),
      },
    ),
    onMutate: async (input) => {
      const queryKey = ["admin", "assumptions", accountId] as const;
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<AssumptionsReview>(queryKey);
      queryClient.setQueryData<AssumptionsReview>(queryKey, (current) => current
        ? applyOptimisticDimensionConfirmation(current, input)
        : current);
      return { previous, queryKey };
    },
    onSuccess: () => {
      // Refresh the light fleet status in the background so its existing
      // "Run checks again" gate appears. Do not rerun this 9-second report.
      void queryClient.invalidateQueries({ queryKey: ["admin", "data-health"] });
      toast.success("Saved. Run the checks again before approving this account.");
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(context.queryKey, context.previous);
      toast.error("That review could not be saved. Check the product and try again.");
    },
  });

  return (
    <section style={shell}>
      <button type="button" style={toggleButton} onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <span style={{ minWidth: 0, textAlign: "left" }}>
          <b style={{ display: "block", color: "var(--text)", fontSize: 14 }}>{review.data ? "Review every assumption" : "Load and review every assumption"}</b>
          <span style={{ display: "block", color: "var(--body)", fontSize: 12, lineHeight: 1.45, marginTop: 3 }}>
            See exactly how every product, pack size, fee, cost, and cash-timing number is being treated before approval.
          </span>
        </span>
        <span aria-hidden="true" style={{ color: "var(--body-faint)", flexShrink: 0 }}>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div style={{ padding: "0 14px 14px" }}>
          {review.isLoading && <p style={muted}>Loading every saved assumption…</p>}
          {review.isError && <p style={{ ...muted, color: "var(--bad)" }}>The assumptions could not load. Close this section and try again.</p>}
          {review.data && (
            <>
              <div style={refreshRow}>
                <span>Loaded {new Date(review.data.generatedAt).toLocaleString()}</span>
                <button type="button" style={refreshButton} disabled={review.isFetching} onClick={() => { void review.refetch(); }}>
                  {review.isFetching ? "Refreshing…" : "Refresh review"}
                </button>
              </div>
              <Summary review={review.data} />
              <Findings findings={review.data.findings} />
              <WarningAcceptance
                findings={warningFindings}
                acceptedIds={acceptedWarningIds}
                note={reviewNote}
                onToggle={(ids, checked) => setAcceptedWarningIds((current) => {
                  const next = new Set(current);
                  for (const id of ids) checked ? next.add(id) : next.delete(id);
                  return next;
                })}
                onNoteChange={setReviewNote}
              />
              <WorkbookQuestions questions={review.data.onboarding.workbookQuestions ?? []} count={review.data.onboarding.unresolvedWorkbookQuestions} />
              <GlobalAssumptions review={review.data} />

              <div style={{ marginTop: 16 }}>
                <div style={headingRow}>
                  <div>
                    <h4 style={heading}>Products and SKUs</h4>
                    <p style={sectionCopy}>Open a product to see every SKU and the exact assumptions used for one sale.</p>
                  </div>
                  <span style={neutralChip}>{review.data.summary.productCount.toLocaleString()} products</span>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                  {review.data.ungroupedSkus.length > 0 && (
                    <UngroupedSkus skus={review.data.ungroupedSkus} currency={review.data.currency} />
                  )}
                  {review.data.products.map((product) => (
                    <ProductReview
                      key={product.groupId}
                      product={product}
                      currency={review.data.currency}
                      open={openProduct === product.groupId}
                      onToggle={() => setOpenProduct(openProduct === product.groupId ? null : product.groupId)}
                      reviewing={confirmDimension.isPending || review.isFetching}
                      onConfirm={(input) => confirmDimension.mutate(input)}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function Summary({ review }: { review: AssumptionsReview }): React.ReactElement {
  const { summary } = review;
  return (
    <div style={summaryGrid}>
      <SummaryItem label="Products" value={summary.productCount} />
      <SummaryItem label="Sold SKUs" value={summary.soldSkuCount} />
      <SummaryItem label="Must fix" value={summary.hardBlockers} tone={summary.hardBlockers > 0 ? "bad" : "good"} />
      <SummaryItem label="Double-check" value={summary.warnings} tone={summary.warnings > 0 ? "warn" : "good"} />
      <SummaryItem label="Estimates" value={summary.estimates} tone={summary.estimates > 0 ? "warn" : "neutral"} />
      <SummaryItem label="Source not recorded" value={summary.sourceNotRecorded} tone={summary.sourceNotRecorded > 0 ? "warn" : "neutral"} />
    </div>
  );
}

function SummaryItem({ label, value, tone = "neutral" }: { label: string; value: number; tone?: "neutral" | "good" | "warn" | "bad" }): React.ReactElement {
  const color = tone === "good" ? "var(--good)" : tone === "warn" ? "var(--warn, #E5A83B)" : tone === "bad" ? "var(--bad)" : "var(--text)";
  return (
    <div style={summaryItem}>
      <b className="dd-num" style={{ color, fontSize: 18 }}>{value.toLocaleString()}</b>
      <span style={{ color: "var(--body)", fontSize: 11.5 }}>{label}</span>
    </div>
  );
}

function Findings({ findings }: { findings: Finding[] }): React.ReactElement | null {
  if (findings.length === 0) return null;
  const grouped = [...findings.reduce((groups, finding) => {
    const key = `${finding.severity}:${finding.title}`;
    const current = groups.get(key);
    if (current) current.count++;
    else groups.set(key, { finding, count: 1 });
    return groups;
  }, new Map<string, { finding: Finding; count: number }>()).values()];
  return (
    <div style={{ marginTop: 14 }}>
      <h4 style={heading}>What needs a decision</h4>
      <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 8 }}>
        {grouped.map(({ finding, count }) => (
          <div key={`${finding.severity}:${finding.title}`} style={{ ...findingBox, borderLeftColor: finding.severity === "hard_blocker" ? "var(--bad)" : "var(--warn, #E5A83B)" }}>
            <div style={headingRow}>
              <b style={{ color: "var(--text)", fontSize: 12.5 }}>{finding.title}{count > 1 ? ` (${count})` : ""}</b>
              <span style={finding.severity === "hard_blocker" ? badChip : warnChip}>{finding.severity === "hard_blocker" ? "Must fix" : "Double-check"}</span>
            </div>
            <p style={{ ...muted, margin: "5px 0 0" }}>{finding.detail}{count > 1 ? ` ${count - 1} more item${count === 2 ? "" : "s"} have the same issue.` : ""}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function WarningAcceptance({ findings, acceptedIds, note, onToggle, onNoteChange }: {
  findings: Finding[];
  acceptedIds: ReadonlySet<string>;
  note: string;
  onToggle: (ids: string[], checked: boolean) => void;
  onNoteChange: (note: string) => void;
}): React.ReactElement | null {
  if (findings.length === 0) return null;
  const groups = [...findings.reduce((result, finding) => {
    const key = `${finding.code}:${finding.title}`;
    const current = result.get(key);
    if (current) current.push(finding);
    else result.set(key, [finding]);
    return result;
  }, new Map<string, Finding[]>()).entries()];
  return (
    <div style={{ ...findingBox, borderLeftColor: "var(--warn, #E5A83B)", marginTop: 14 }}>
      <b style={{ color: "var(--text)", fontSize: 13 }}>Accept temporary assumptions one category at a time</b>
      <p style={{ ...muted, margin: "5px 0 10px" }}>
        These yellow items are not proven facts. Check a category only after you have reviewed it and are comfortable launching with it temporarily.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {groups.map(([key, group]) => {
          const ids = group.map((finding) => finding.id);
          const checked = ids.every((id) => acceptedIds.has(id));
          return (
            <label key={key} style={{ display: "flex", alignItems: "flex-start", gap: 8, color: "var(--body)", fontSize: 12.5, lineHeight: 1.45 }}>
              <input type="checkbox" checked={checked} onChange={(event) => onToggle(ids, event.currentTarget.checked)} style={{ marginTop: 2 }} />
              <span><b style={{ color: "var(--text)" }}>{group[0]!.title}</b>{group.length > 1 ? ` (${group.length} items)` : ""}</span>
            </label>
          );
        })}
      </div>
      <label style={{ display: "block", marginTop: 12, color: "var(--body)", fontSize: 12 }}>
        Launch-review note
        <textarea
          value={note}
          maxLength={500}
          onChange={(event) => onNoteChange(event.currentTarget.value)}
          placeholder="Why are these temporary assumptions safe enough to launch with?"
          style={{ width: "100%", minHeight: 68, resize: "vertical", marginTop: 5, border: "1px solid var(--border)", borderRadius: 8, padding: "9px 10px", background: "var(--surface)", color: "var(--text)", font: "inherit", boxSizing: "border-box" }}
        />
      </label>
      <p style={{ ...muted, margin: "6px 0 0" }}>The checked categories and this note are saved with the final approval.</p>
    </div>
  );
}

function WorkbookQuestions({ questions, count }: { questions: Array<{ id: string; question: string }>; count: number }): React.ReactElement | null {
  if (count === 0) return null;
  return (
    <div style={{ ...findingBox, borderLeftColor: "var(--warn, #E5A83B)", marginTop: 14 }}>
      <div style={headingRow}>
        <b style={{ color: "var(--text)", fontSize: 13 }}>The uploaded cost file still has {count} unanswered question{count === 1 ? "" : "s"}</b>
        <span style={warnChip}>Unresolved</span>
      </div>
      {questions.length > 0 ? (
        <ol style={{ color: "var(--body)", fontSize: 12.5, lineHeight: 1.5, paddingLeft: 20, margin: "8px 0 0" }}>
          {questions.map((question) => <li key={question.id}>{question.question}</li>)}
        </ol>
      ) : (
        <p style={{ ...muted, margin: "6px 0 0" }}>Open product costs to answer the remaining workbook question before approval.</p>
      )}
    </div>
  );
}

function GlobalAssumptions({ review }: { review: AssumptionsReview }): React.ReactElement {
  const { global, currency } = review;
  return (
    <div style={{ marginTop: 16 }}>
      <h4 style={heading}>Account-wide assumptions</h4>
      <p style={sectionCopy}>These numbers can affect every product, so review them before individual SKUs.</p>
      <ParkedCostsNotice costs={global.parkedCosts} />
      <div style={globalGrid}>
        <GlobalCard title="Monthly overhead" source={global.overhead[0]?.source}>
          {global.overhead.length === 0 ? <Missing label="No monthly overhead is saved" /> : global.overhead.slice(0, 3).map((row) => (
            <div key={`${row.effectiveDate}:${row.amount}`} style={valueRow}>
              <span>{formatMoney(row.amount, row.currency)}</span>
              <small>from {formatDate(row.effectiveDate)}{row.approvedAt ? " · accounting reviewed" : " · not accounting reviewed"}</small>
            </div>
          ))}
        </GlobalCard>

        <GlobalCard title="Payment processing" source={global.processing?.source}>
          {!global.processing ? <Missing label="No processing cost is saved" /> : (
            <>
              <div style={valueRow}>
                <span>{formatReviewPercent(global.processing.blendedPercent)} + {formatMoney(global.processing.blendedFixed, currency)}</span>
                <small>saved blended amount per payment</small>
              </div>
              {global.processing.processors.map((processor) => (
                <div key={processor.name} style={compactLine}>
                  <span>{processor.name}</span>
                  <span>{formatReviewPercent(processor.percent)} + {formatMoney(processor.fixed, currency)} · {processor.sharePercent.toFixed(1)}%</span>
                  <SourceBadge source={processor.source} />
                </div>
              ))}
            </>
          )}
        </GlobalCard>

        <GlobalCard title="Cash settlement and reserves" source={global.payout?.source}>
          {!global.payout ? <Missing label="Cash settlement timing is not saved" /> : (
            <>
              <div style={valueRow}><span>{global.payout.settlementDays.toFixed(1)} days</span><small>average time until cash lands</small></div>
              <div style={compactLine}><span>Reserve</span><span>{global.payout.reservePercent.toFixed(1)}% for {global.payout.reserveDays.toFixed(0)} days</span></div>
            </>
          )}
        </GlobalCard>

        <GlobalCard title="Other account-wide costs">
          {global.otherCosts.length === 0 ? <Missing label="No other account-wide cost is saved" /> : global.otherCosts.map((cost) => (
            <div key={cost.id} style={compactLine}>
              <span>{cost.name}</span>
              <span>{cost.unit === "percent" ? formatReviewPercent(cost.amount) : formatMoney(cost.amount, currency)} · {cost.scope === "all" ? "every product" : `${cost.scope.length} products`}</span>
              <SourceBadge source={cost.source} />
            </div>
          ))}
        </GlobalCard>

        <GlobalCard title="Supplier payment timing" source={global.supplierSchedule?.source}>
          {!global.supplierSchedule ? <Missing label="Supplier payment timing is not saved" /> : (
            <>
              {global.supplierSchedule.tranches.length === 0
                ? <Missing label="An exact supplier payment schedule is not saved" />
                : global.supplierSchedule.tranches.map((tranche, index) => (
                    <div key={`${tranche.anchor}:${tranche.offsetDays}:${index}`} style={compactLine}>
                      <span>Payment {index + 1}</span>
                      <span>{supplierPaymentTimingText(tranche)}</span>
                    </div>
                  ))}
              <div style={compactLine}><span>Card delay included above</span><span>{global.supplierSchedule.supplierCardFloatDays === null ? "Not saved" : `${global.supplierSchedule.supplierCardFloatDays} days`}</span></div>
            </>
          )}
        </GlobalCard>

        <GlobalCard title="Inventory financing" source={global.inventoryFinancing?.source}>
          {!global.inventoryFinancing ? <Missing label="No inventory financing is saved" /> : (
            <>
              <div style={compactLine}><span>Financed</span><span>{global.inventoryFinancing.financedPercent.toFixed(1)}%</span></div>
              <div style={compactLine}><span>Annual rate</span><span>{global.inventoryFinancing.annualRatePercent.toFixed(2)}%</span></div>
            </>
          )}
        </GlobalCard>
      </div>
    </div>
  );
}

export function ParkedCostsNotice({ costs }: { costs: string[] }): React.ReactElement | null {
  if (costs.length === 0) return null;
  return (
    <div style={parkedCostsBox}>
      <div style={headingRow}>
        <b style={{ color: "var(--text)", fontSize: 13 }}>Saved costs are not included in the math</b>
        <span style={warnChip}>{costs.length} {costs.length === 1 ? "cost" : "costs"} not counted</span>
      </div>
      <p style={{ ...muted, margin: "6px 0 0" }}>
        These were saved during setup because the dashboard could not apply them safely. They do not reduce contribution margin, profit, or cash. Review each one before you approve the account.
      </p>
      <div style={parkedCostsList}>
        {costs.map((cost, index) => (
          <div key={`${cost}:${index}`} style={parkedCostRow}>{cost}</div>
        ))}
      </div>
    </div>
  );
}

function GlobalCard({ title, source, children }: { title: string; source?: Source; children: React.ReactNode }): React.ReactElement {
  return (
    <div style={globalCard}>
      <div style={headingRow}>
        <b style={{ color: "var(--text)", fontSize: 12.5 }}>{title}</b>
        {source && <SourceBadge source={source} />}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 9 }}>{children}</div>
    </div>
  );
}

function ProductReview({ product, currency, open, onToggle, reviewing, onConfirm }: {
  product: ReviewProduct;
  currency: string;
  open: boolean;
  onToggle: () => void;
  reviewing: boolean;
  onConfirm: (input: DimensionConfirmation) => void;
}): React.ReactElement {
  const blockers = product.findings.filter((finding) => finding.severity === "hard_blocker").length;
  const warnings = product.findings.length - blockers;
  return (
    <div style={productShell}>
      <button type="button" style={productButton} onClick={onToggle} aria-expanded={open}>
        <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <b style={{ color: "var(--text)", fontSize: 13.5 }}>{product.name}</b>
            {blockers > 0 && <span style={badChip}>{blockers} must fix</span>}
            {blockers === 0 && warnings > 0 && <span style={warnChip}>{warnings} to double-check</span>}
            {product.findings.length === 0 && <span style={goodChip}>No problems found</span>}
          </span>
          <span style={{ display: "block", color: "var(--body)", fontSize: 11.5, marginTop: 4 }}>
            {product.skuCount.toLocaleString()} SKU{product.skuCount === 1 ? "" : "s"} · {product.soldSkuCount.toLocaleString()} sold · {formatMoney(product.revenue, currency)} revenue · {product.kind} · {product.type ?? "type not set"}
          </span>
          {product.aliases.length > 0 && <span style={{ display: "block", color: "var(--body-faint)", fontSize: 11, marginTop: 3 }}>Names found: {product.aliases.slice(0, 8).join(", ")}{product.aliases.length > 8 ? `, and ${product.aliases.length - 8} more` : ""}</span>}
          {product.reviewedAliases.length > 0 && <span style={{ display: "block", color: "var(--body-faint)", fontSize: 11, marginTop: 3 }}>Remembered names: {product.reviewedAliases.map((alias) => alias.matchType === "prefix" ? `${alias.alias}…` : alias.alias).join(", ")}</span>}
        </span>
        <span aria-hidden="true" style={{ color: "var(--body-faint)", flexShrink: 0 }}>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div style={{ padding: "0 12px 12px" }}>
          <ProductRules product={product} currency={currency} />
          <DimensionReview
            product={product}
            reviewing={reviewing}
            onConfirm={onConfirm}
          />
          <Findings findings={product.findings} />
          <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 10 }}>
            {product.skus.map((sku) => <SkuReview key={sku.skuId} sku={sku} currency={currency} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function DimensionReview({ product, reviewing, onConfirm }: {
  product: ReviewProduct;
  reviewing: boolean;
  onConfirm: (input: DimensionConfirmation) => void;
}): React.ReactElement | null {
  const relevant = product.skus.filter((sku) => sku.sold && sku.classification.status !== "excluded" && sku.classification.status !== "retired");
  if (relevant.length === 0) return null;
  const familyPending = relevant.filter((sku) => sku.classification.familyStatus !== "confirmed");
  const unitsPending = relevant.filter((sku) => sku.classification.unitsStatus !== "confirmed");
  const familyHuman = relevant.every((sku) => sku.classification.familyReviewBasis === "human");
  const unitsHuman = relevant.every((sku) => sku.classification.unitsReviewBasis === "human");
  const confirmation = dimensionConfirmationForProduct(product);
  return (
    <div style={dimensionReviewBox}>
      <div>
        <b style={{ color: "var(--text)", fontSize: 12 }}>Check the product and units</b>
        <p style={{ ...muted, margin: "4px 0 0" }}>Confirm where these SKUs belong and how many real units each sale ships. One button records every open check separately.</p>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {familyPending.length === 0 && <span style={goodChip}>{familyHuman ? "Product family checked by a person" : "Product family passed saved rules"}</span>}
        {unitsPending.length === 0 && <span style={goodChip}>{unitsHuman ? "Units per sale checked by a person" : "Units per sale passed saved rules"}</span>}
        {confirmation && <button type="button" style={reviewButton} disabled={reviewing} onClick={() => onConfirm(confirmation)}>{reviewing ? "Saving…" : confirmation.label}</button>}
      </div>
    </div>
  );
}

function ProductRules({ product, currency }: { product: ReviewProduct; currency: string }): React.ReactElement {
  const { savedRules } = product;
  return (
    <div style={rulesRow}>
      <span><b>Shipping rule:</b> {savedRules.fulfillmentFormula ? `${formatMoney(savedRules.fulfillmentFormula.base, currency)} first unit + ${formatMoney(savedRules.fulfillmentFormula.perExtraUnit, currency)} each extra` : savedRules.fulfillmentFlat ? "Flat for every pack size" : "Saved per SKU"}</span>
      <span><b>Packaging:</b> {savedRules.packagingShownSeparately ? "Shown separately" : "Not recorded separately"}</span>
      {savedRules.supplierPaymentOverride && <ProductSupplierPaymentOverride schedule={savedRules.supplierPaymentOverride} />}
      {savedRules.pendingCatalogAssumptions && <span style={{ color: "var(--bad)" }}><b>Old assumptions need review after a catalog change</b></span>}
    </div>
  );
}

export function ProductSupplierPaymentOverride({ schedule }: { schedule: SupplierPaymentSchedule }): React.ReactElement {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
      <b>Supplier timing override:</b>
      {schedule.tranches.map((tranche, index) => (
        <span key={`${tranche.anchor}:${tranche.offsetDays}:${index}`}>
          {index > 0 ? " · " : ""}{supplierPaymentTimingText(tranche)}
        </span>
      ))}
      <SourceBadge source={schedule.source} />
    </span>
  );
}

function SkuReview({ sku, currency }: { sku: ReviewSku; currency: string }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const status = sku.findings.some((finding) => finding.severity === "hard_blocker")
    ? "conflict"
    : sku.findings.length > 0
      ? "unverified"
      : sku.source.status;
  return (
    <details style={skuShell} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary style={skuSummary}>
        <span style={{ minWidth: 0 }}>
          <b style={{ color: "var(--text)", fontSize: 12.5 }}>{sku.rawVariantName || sku.rawProductName || sku.skuCode}</b>
          <small style={skuCode}>{sku.skuCode}</small>
        </span>
        <Metric label="Orders" value={sku.orders.toLocaleString()} />
        <Metric label="Units / sale" value={formatNumber(sku.math.unitsPerSale)} />
        <Metric label="Product cost / unit" value={formatMoneyOrMissing(sku.math.productCostPerTrueUnit, currency)} />
        <Metric label="Product cost / sale" value={formatMoneyOrMissing(sku.math.productCostPerSale, currency)} />
        <Metric label="Fulfillment / shipping" value={formatMoneyOrMissing(sku.math.fulfillmentShippingPerSale, currency)} />
        <SourceBadge source={{ ...sku.source, status }} />
      </summary>
      {open && <div style={skuBody}>
        <div style={formulaGrid}>
          <FormulaLine label="Classification" value={`${sku.classification.variantLabel ? `${sku.classification.variantLabel} · ` : ""}${sku.classification.offerLabel ?? "Standard offer"} · ${formatNumber(sku.classification.unitsPerSale)} ${sku.classification.unitsPerSale === 1 ? "unit" : "units"} per sale`} />
          <FormulaLine label="Product family review" value={familyReviewLabel(sku.classification)} />
          <FormulaLine label="Units review" value={unitsReviewLabel(sku.classification)} />
          <FormulaLine label="Product cost" value={sku.math.productCostPerTrueUnit === null || sku.math.unitsPerSale === null ? "Missing" : `${formatMoney(sku.math.productCostPerTrueUnit, currency)} × ${formatNumber(sku.math.unitsPerSale)} units = ${formatMoneyOrMissing(sku.math.productCostPerSale, currency)}`} />
          <FormulaLine label="Product cost source" value={sku.productCostSource.label} />
          <FormulaLine label="Payment processing" value={sku.math.processingPercent === null ? "Missing" : `${formatReviewPercent(sku.math.processingPercent)} of the payment + ${formatMoneyOrMissing(sku.math.processingFixedPerSale, currency)}`} />
          <FormulaLine label="Known fixed variable costs" value={formatMoneyOrMissing(sku.math.knownFixedVariableCostsPerSale, currency)} />
          <FormulaLine label="Sales found" value={`${formatMoney(sku.revenue, currency)} from ${sku.orders.toLocaleString()} orders · ${sku.soldPacks.toLocaleString()} SKU packs sold${sku.physicalUnits === null ? " · physical units not verified" : ` · ${sku.physicalUnits.toLocaleString()} physical units`}`} />
          <FormulaLine label="Effective date" value={sku.cmInput?.effectiveDate ? formatDate(sku.cmInput.effectiveDate) : "No active cost row"} />
          {sku.inventoryTiming && <FormulaLine label="Inventory timing" value={`${sku.inventoryTiming.leadTimeDays} lead days · ${sku.inventoryTiming.paymentTermsDays} payment days${sku.inventoryTiming.supplierName ? ` · ${sku.inventoryTiming.supplierName}` : ""}`} />}
        </div>
        {sku.cmInput && (
          <div style={{ marginTop: 10 }}>
            <b style={{ color: "var(--text)", fontSize: 11.5 }}>Everything else saved on this SKU</b>
            <div style={costChips}>
              <CostChip label="Pick and pack" value={sku.cmInput.pickPackPerSale} currency={currency} />
              <CostChip label="Returns handling" value={sku.cmInput.returnsProcessingPerSale} currency={currency} />
              <CostChip label="Inbound freight" value={sku.cmInput.inboundFreightPerSale} currency={currency} />
              <CostChip label="Labor" value={sku.cmInput.laborPerSale} currency={currency} />
              <CostChip label="Storage / month" value={sku.cmInput.storageFeePerMonth} currency={currency} />
              <CostChip label="Observed refund rate" value={sku.cmInput.refundRatePercent} percent />
              {sku.cmInput.otherCosts.map((cost) => (
                <span key={`${cost.name}:${cost.kind}:${cost.amount}`} style={costChip}>
                  {cost.name}: {cost.unit === "percent" ? formatReviewPercent(cost.amount) : formatMoney(cost.amount, currency)}{cost.firstOrderOnly ? " · first order only" : ""}
                  {cost.source && <SourceBadge source={cost.source} />}
                </span>
              ))}
            </div>
          </div>
        )}
        {sku.fulfillmentEvidence.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <b style={{ color: "var(--text)", fontSize: 11.5 }}>Shipping and packaging source</b>
            <div style={costChips}>{sku.fulfillmentEvidence.map((evidence) => (
              <span key={`${evidence.platform}:${evidence.workbookName}:${evidence.sheetName}`} style={costChip}>
                {friendlyPlatform(evidence.platform)}: {evidence.shipping.header} {formatMoney(evidence.shipping.amount, currency)} + {evidence.packaging.header} {formatMoney(evidence.packaging.amount, currency)} = {formatMoney(evidence.total, currency)} · {evidence.workbookName}, {evidence.sheetName}
              </span>
            ))}</div>
          </div>
        )}
        {sku.bundleComponents.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <b style={{ color: "var(--text)", fontSize: 11.5 }}>Bundle contents</b>
            <div style={costChips}>{sku.bundleComponents.map((component) => <span key={component.id} style={costChip}>{component.quantity} × {component.label}</span>)}</div>
          </div>
        )}
        <Findings findings={sku.findings} />
      </div>}
    </details>
  );
}

function UngroupedSkus({ skus, currency }: { skus: ReviewSku[]; currency: string }): React.ReactElement {
  return (
    <div style={{ ...productShell, borderColor: "color-mix(in srgb, var(--bad) 45%, var(--border))" }}>
      <div style={{ padding: 12 }}>
        <div style={headingRow}><b style={{ color: "var(--text)", fontSize: 13.5 }}>SKUs without a product</b><span style={badChip}>{skus.length} must fix</span></div>
        <p style={sectionCopy}>These sold items must be placed under the right product before their product-level numbers can be trusted.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 9 }}>{skus.map((sku) => <SkuReview key={sku.skuId} sku={sku} currency={currency} />)}</div>
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }): React.ReactElement {
  return <span style={metric}><small>{label}</small><b>{value}</b></span>;
}

function FormulaLine({ label, value }: { label: string; value: string }): React.ReactElement {
  return <div style={formulaLine}><span>{label}</span><b>{value}</b></div>;
}

function CostChip({ label, value, currency, percent = false }: { label: string; value: number; currency?: string; percent?: boolean }): React.ReactElement {
  return <span style={costChip}>{label}: {percent ? formatReviewPercent(value) : formatMoney(value, currency ?? "USD")}</span>;
}

function SourceBadge({ source }: { source: Source }): React.ReactElement {
  const style = source.status === "confirmed"
    ? goodChip
    : source.status === "estimate" || source.status === "unverified"
      ? warnChip
      : badChip;
  const label = source.status === "confirmed"
    ? "Confirmed"
    : source.status === "estimate"
      ? "Starting estimate"
      : source.status === "unverified"
        ? "Source not verified"
        : source.status === "missing"
          ? "Missing"
          : "Conflict";
  return <span style={style} title={[source.label, source.detail].filter(Boolean).join("\n")}>{label}</span>;
}

function friendlyPackSource(source: NonNullable<ReviewSku["classification"]["packSource"]>): string {
  if (source === "explicit") return "the product name";
  if (source === "multiplier") return "an explicit multiplier";
  if (source === "manual") return "a manual edit";
  if (source === "rule") return "a product rule";
  if (source === "bare") return "a bare number";
  return "a one-unit default";
}

function familyReviewLabel(classification: ReviewSku["classification"]): string {
  if (classification.familyStatus !== "confirmed") return "Needs review";
  if (classification.familyReviewBasis === "human") return "Checked by a person";
  if (classification.familyReviewBasis === "reviewed_alias") return "Matched a reviewed product name";
  return "Passed an automatic rule check";
}

function unitsReviewLabel(classification: ReviewSku["classification"]): string {
  if (classification.unitsStatus !== "confirmed") return "Needs review";
  if (classification.unitsReviewBasis === "human") return "Checked by a person";
  if (classification.unitsReviewBasis === "product_name") return `Read from ${friendlyPackSource(classification.packSource ?? "explicit")}`;
  return `Passed an automatic rule check${classification.packSource ? ` · based on ${friendlyPackSource(classification.packSource)}` : ""}`;
}

function friendlyPlatform(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function Missing({ label }: { label: string }): React.ReactElement {
  return <span style={{ color: "var(--body-faint)", fontSize: 12 }}>{label}</span>;
}

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function formatMoneyOrMissing(amount: number | null, currency: string): string {
  return amount === null ? "Missing" : formatMoney(amount, currency);
}

function formatNumber(value: number | null): string {
  return value === null ? "Missing" : Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
}

function formatDate(value: string): string {
  const date = new Date(`${value.length === 10 ? `${value}T00:00:00` : value}`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

export function supplierPaymentTimingText(tranche: SupplierPaymentSchedule["tranches"][number]): string {
  const percent = Number.isInteger(tranche.percent)
    ? tranche.percent.toFixed(0)
    : tranche.percent.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
  if (tranche.anchor === "at_po") return `${percent}% of the cash leaves when the purchase order is placed`;
  const days = tranche.offsetDays;
  const timing = days === 0 ? "the same day" : `${days} ${days === 1 ? "day" : "days"} after`;
  if (tranche.anchor === "after_po") return `${percent}% of the cash leaves ${timing} the purchase order is placed`;
  if (tranche.anchor === "after_arrival") return `${percent}% of the cash leaves ${timing} inventory arrives`;
  return `${percent}% of the cash leaves ${timing} the customer buys`;
}

const shell: React.CSSProperties = { background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", marginBottom: 12, overflow: "hidden" };
const toggleButton: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, width: "100%", padding: 14, background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit" };
const refreshRow: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, color: "var(--body-faint)", fontSize: 11.5, margin: "0 0 8px" };
const refreshButton: React.CSSProperties = { border: "1px solid var(--border)", background: "var(--surface)", color: "var(--body)", borderRadius: 7, padding: "6px 9px", fontFamily: "inherit", fontSize: 11.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" };
const summaryGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8, marginTop: 2 };
const summaryItem: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 2, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", padding: "9px 11px" };
const headingRow: React.CSSProperties = { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 };
const heading: React.CSSProperties = { color: "var(--text)", fontSize: 14, fontWeight: 750, margin: 0 };
const sectionCopy: React.CSSProperties = { color: "var(--body)", fontSize: 12, lineHeight: 1.45, margin: "4px 0 0" };
const muted: React.CSSProperties = { color: "var(--body)", fontSize: 12.5, lineHeight: 1.5 };
const findingBox: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderLeft: "3px solid", borderRadius: "var(--r-sm)", padding: "10px 11px" };
const parkedCostsBox: React.CSSProperties = { ...findingBox, borderLeftColor: "var(--warn, #E5A83B)", marginTop: 10 };
const parkedCostsList: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 6, marginTop: 9 };
const parkedCostRow: React.CSSProperties = { color: "var(--text)", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", padding: "7px 9px", fontSize: 11.5, lineHeight: 1.4 };
const baseChip: React.CSSProperties = { fontSize: 10.5, fontWeight: 750, padding: "3px 8px", borderRadius: 999, whiteSpace: "nowrap", flexShrink: 0 };
const goodChip: React.CSSProperties = { ...baseChip, color: "var(--good)", background: "color-mix(in srgb, var(--good) 14%, transparent)" };
const warnChip: React.CSSProperties = { ...baseChip, color: "var(--warn, #E5A83B)", background: "color-mix(in srgb, var(--warn, #E5A83B) 16%, transparent)" };
const badChip: React.CSSProperties = { ...baseChip, color: "var(--bad)", background: "color-mix(in srgb, var(--bad) 14%, transparent)" };
const neutralChip: React.CSSProperties = { ...baseChip, color: "var(--body)", background: "var(--surface-3)" };
const globalGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 8, marginTop: 10 };
const globalCard: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", padding: 11, minWidth: 0 };
const valueRow: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 2, color: "var(--text)", fontSize: 16, fontWeight: 750 };
const compactLine: React.CSSProperties = { display: "grid", gridTemplateColumns: "minmax(90px, 1fr) minmax(110px, auto) auto", alignItems: "center", gap: 7, color: "var(--body)", fontSize: 11.5, lineHeight: 1.35 };
const productShell: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", overflow: "hidden" };
const productButton: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, width: "100%", padding: 12, background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit" };
const rulesRow: React.CSSProperties = { display: "flex", flexWrap: "wrap", gap: "7px 14px", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", color: "var(--body)", fontSize: 11.5, lineHeight: 1.4, padding: "9px 10px" };
const dimensionReviewBox: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 10, padding: "10px 11px", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", background: "var(--surface-2)" };
const reviewButton: React.CSSProperties = { border: "1px solid color-mix(in srgb, var(--accent) 35%, var(--border))", background: "var(--surface)", color: "var(--accent)", borderRadius: 7, padding: "7px 10px", fontFamily: "inherit", fontSize: 11.5, fontWeight: 700, cursor: "pointer" };
const skuShell: React.CSSProperties = { background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", overflowX: "auto" };
const skuSummary: React.CSSProperties = { display: "grid", gridTemplateColumns: "minmax(180px, 2fr) repeat(5, minmax(90px, 1fr)) auto", alignItems: "center", gap: 9, minWidth: 960, padding: "9px 10px", cursor: "pointer", listStyle: "none" };
const skuCode: React.CSSProperties = { display: "block", color: "var(--body-faint)", fontSize: 10.5, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis" };
const metric: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 2, color: "var(--text)", fontSize: 11.5, minWidth: 0 };
const skuBody: React.CSSProperties = { borderTop: "1px solid var(--border)", padding: 10, background: "var(--surface)" };
const formulaGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 7 };
const formulaLine: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 3, border: "1px solid var(--border)", borderRadius: "var(--r-sm)", padding: "8px 9px", color: "var(--body)", fontSize: 10.5, lineHeight: 1.35 };
const costChips: React.CSSProperties = { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 7 };
const costChip: React.CSSProperties = { color: "var(--body)", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 999, padding: "4px 8px", fontSize: 10.5 };
